import StyledButton from '@/components/styled/StyledButton';
import StyledContainer from '@/components/styled/StyledContainer';
import StyledFormHeaderWithBreadcrumb from '@/components/styled/StyledFormHeaderWithBreadcrumb';
import ArrowLeftIcon from '@/assets/icons/arrow-left.svg?react';
import SaveIcon from '@/assets/icons/save-02.svg?react';
import PlusIcon from '@/assets/icons/plus.svg?react';
import { useLocation, useNavigate } from 'react-router';
import { PATHS } from '@/constants/Paths';
import { buildBenefitsListSearch } from '@/utils/searchParams/buildBenefitsListSearch';
import { getListSearchFromLocation } from '@/utils/searchParams/getListSearchFromLocation';
import StyledButtonLoader from '@/components/styled/StyledButtonLoader';
import HorizontalLine from '@/components/ui/HorizontalLine';
import {
  FormProvider,
  SubmitHandler,
  useForm,
  useWatch,
} from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useGetAllMySuppliers } from '@/api/supplier.api';
import { ArrowRightIcon } from 'lucide-react';
import { useMemo, useState } from 'react';
import clsx from 'clsx';
import {
  useCreateVehicleWithChauffeur,
  useUpdateVehicleWithChauffeur,
} from '@/hooks/useVehicleWithChauffeur';
import { SupplierStatus } from '@/types/suppliers/SupplierStatus';
import { useToast } from '@/hooks/useToast';
import { useTranslation } from '@/i18n';
import { useTranslatedConst } from '@/hooks/useTranslatedConst';
import {
  VehicleWithChauffeurFormDataWithTranslations,
  VehicleWithChauffeurSchemaWithTranslations,
} from '@/i18n/translation-type-utils';
import { useResourceLanguage } from '@/hooks/useResourceLanguage';
import { useTranslateFields } from '@/hooks/useTranslate';
import LanguageSwitchWithTranslateBtn from '@/components/translation/LanguageSwitchWithTranslateBtn';
import StyledStepper from '@/components/styled/StyledStepper';
import { GENERAL_BENEFITS_STEPS } from '@/constants/steppers/generalBenefitsSteps';
import BenefitPricingSummary from '@/components/benefits/BenefitPricingSummary';
import VehicleWithChauffeurConfiguration from '@/components/vehicleWithChauffeur/VehicleWithChauffeurConfiguration';
import { MarginRateSchema } from '@/types/common/margin-rate.schema';

type SubmitFormType = (
  isDraft?: boolean,
) => SubmitHandler<VehicleWithChauffeurFormDataWithTranslations>;

const CreateVehicleWithChauffeurPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const getTranslatedConst = useTranslatedConst();
  const methods = useForm<VehicleWithChauffeurFormDataWithTranslations>({
    resolver: zodResolver(VehicleWithChauffeurSchemaWithTranslations),
    mode: 'onChange',
    defaultValues: {
      name: undefined,
      supplier: undefined,
      currency: undefined,
      country: undefined,
      marginRate: undefined,
      model: undefined,
      shortDescription: undefined,
      longDescription: undefined,
      images: [],
      maxCapacity: undefined,
      tarifs: [
        {
          name: undefined,
          periods: [
            {
              startDate: undefined,
              endDate: undefined,
              applicationDays: [],
            },
          ],
          pricePartitions: [
            {
              price: undefined,
              from: 1,
              to: 1,
            },
          ],
        },
      ],
      tarifType: undefined,
      customTarif: undefined,
      childAgeFrom: undefined,
      childAgeTo: undefined,
    },
  });

  const {
    control,
    getValues,
    formState: { isValid, errors },
    handleSubmit,
    setValue,
  } = methods;

  const { data: suppliers } = useGetAllMySuppliers();
  const { mutateAsync: createVehicleWithChauffeur } =
    useCreateVehicleWithChauffeur();
  const { mutateAsync: updateVehicleWithChauffeur } =
    useUpdateVehicleWithChauffeur();

  const currentName = useWatch({ control, name: 'name' });
  const selectedSupplier = useWatch({ control, name: 'supplier' });
  const selectedCountry = useWatch({ control, name: 'country' });

  const [vehicleWithChauffeurId, setVehicleWithChauffeurId] = useState<
    string | null
  >(null);

  const errorsLength = useMemo(() => Object.keys(errors).length, [errors]);

  const { language, setLanguage } = useResourceLanguage();
  const { translateFields, isTranslating } = useTranslateFields({
    targetLanguage: language,
    control,
    setValue,
    fields: [
      'benefit.name',
      'model',
      'shortDescription',
      'longDescription',
    ] as const,
  });

  const [currStep, setCurrStep] = useState(0);

  const { toastAsync } = useToast();

  const submitForm: SubmitFormType =
    (isDraft = true) =>
    async (data) => {
      const formData = new FormData();
      const addIf = (value: string | Blob | undefined, key: string) => {
        if (value !== undefined) formData.append(key, value);
      };
      if (!isDraft) formData.append('status', SupplierStatus.PUBLISHED);
      addIf(data.name, 'name');
      addIf(data.supplier?.value, 'supplierId');
      addIf(data.currency?.value, 'currency');
      addIf(data.country?.value, 'country');
      const parsedMarginRate = MarginRateSchema.safeParse(data.marginRate);
      if (parsedMarginRate.success && parsedMarginRate.data !== undefined)
        formData.append('marginRate', String(parsedMarginRate.data));
      addIf(data.model, 'model');
      addIf(data.shortDescription, 'shortDescription');
      addIf(data.longDescription, 'longDescription');
      if (data.maxCapacity)
        formData.append('maxCapacity', String(data.maxCapacity));
      if (data.tarifs) formData.append('pricings', JSON.stringify(data.tarifs));
      data.images.forEach((image) => formData.append('files', image));
      formData.append('translations', JSON.stringify(data.translations));
      if (!vehicleWithChauffeurId) {
        const result = await toastAsync(createVehicleWithChauffeur(formData), {
          success: t('benefits.benefitCreatedSuccess'),
        });
        setVehicleWithChauffeurId(result.data.benefitid);
      } else {
        await toastAsync(
          updateVehicleWithChauffeur({
            benefitId: vehicleWithChauffeurId,
            data: formData,
          }),
          {
            success: t('benefits.benefitUpdatedSuccess'),
          },
        );
      }
      if (!isDraft)
        navigate(
          PATHS.home.benefits.list +
            getListSearchFromLocation(location, buildBenefitsListSearch),
        );
    };

  return (
    <FormProvider {...methods}>
      <StyledContainer>
        <StyledFormHeaderWithBreadcrumb
          title={currentName || t('vehiclesWithChauffeur.newVehicle')}
          breadcrumb={
            <StyledButton
              variant="link"
              title={t('common.backToList')}
              className="text-sm text-[#3D3D3D] pl-0"
              onClick={() =>
                navigate(
                  PATHS.home.benefits.list +
                    getListSearchFromLocation(
                      location,
                      buildBenefitsListSearch,
                    ),
                )
              }
              icon={<ArrowLeftIcon />}
            />
          }
        >
          <StyledButton
            title={t('common.cancel')}
            onClick={() =>
              navigate(
                PATHS.home.benefits.list +
                  getListSearchFromLocation(location, buildBenefitsListSearch),
              )
            }
            variant="outlined"
            className="font-semibold gap-1 px-3 py-2"
          />
          <StyledButtonLoader
            title={t('common.saveAsDraft')}
            successTitle={t('common.draftSaved')}
            disabled={
              currentName == undefined ||
              selectedSupplier == undefined ||
              selectedCountry == undefined
            }
            className="font-semibold gap-1 px-3 py-2"
            onClick={() => submitForm(true)(getValues())}
            icon={<SaveIcon className="w-5 h-5" />}
          />
        </StyledFormHeaderWithBreadcrumb>
        <StyledStepper
          activeStep={currStep}
          steps={getTranslatedConst(GENERAL_BENEFITS_STEPS)}
          setStep={(step: number) => setCurrStep(step)}
        />
        <HorizontalLine />
        <LanguageSwitchWithTranslateBtn
          translateFields={() =>
            translateFields({ translateToAllLocales: true })
          }
          isTranslating={isTranslating}
          language={language}
          setLanguage={setLanguage}
        />
        {currStep === 0 ? (
          <>
            <VehicleWithChauffeurConfiguration
              control={control}
              suppliers={suppliers}
              isEditEnabled={true}
              language={language}
              translateFields={(fields) =>
                translateFields({ fieldsToTranslate: fields })
              }
              isTranslating={isTranslating}
              errorsLength={errorsLength}
            />
            <div className="flex flex-row-reverse p-6">
              <StyledButtonLoader
                title={t('common.saveAsDraft')}
                successTitle={t('common.draftSaved')}
                disabled={
                  currentName == undefined ||
                  selectedSupplier == undefined ||
                  selectedCountry == undefined
                }
                className="font-semibold gap-1 px-3 py-2"
                onClick={() => submitForm(true)(getValues())}
                icon={<SaveIcon className="w-5 h-5" />}
              />
            </div>
            <div className="flex flex-row-reverse p-6">
              <StyledButtonLoader
                title={t('vehiclesWithChauffeur.publishVehicle')}
                className="font-semibold gap-1 px-3 py-2"
                onClick={handleSubmit(submitForm(false))}
                successTitle={t('common.publishedSuccess')}
                hasSucceeded={!errorsLength}
                errorTitle={t('common.checkRequiredFields')}
                icon={
                  <PlusIcon
                    className={clsx('w-5 h-5', {
                      'fill-white': isValid,
                      'fill-gray-400': !isValid,
                    })}
                  />
                }
              />
            </div>
            <div className="flex flex-row-reverse px-6 pb-6 md:w-fit md:sticky md:left-full md:bottom-0">
              <StyledButton
                title={t('common.pricingSummary')}
                icon={<ArrowRightIcon className="w-5 h-5" />}
                iconPosition="right"
                className="font-semibold gap-1 px-3 py-2"
                onClick={() => {
                  setCurrStep(1);
                  window.scrollTo(0, 0);
                }}
              />
            </div>
          </>
        ) : (
          <>
            <BenefitPricingSummary control={control} />
            <div className="flex flex-row-reverse p-6 md:sticky md:bottom-0 z-10 bg-white">
              <StyledButtonLoader
                title={t('vehiclesWithChauffeur.publishVehicle')}
                className="font-semibold gap-1 px-3 py-2"
                onClick={handleSubmit(submitForm(false))}
                successTitle={t('common.publishedSuccess')}
                hasSucceeded={!errorsLength}
                errorTitle={t('common.checkRequiredFields')}
                icon={
                  <PlusIcon
                    className={clsx('w-5 h-5', {
                      'fill-white': isValid,
                      'fill-gray-400': !isValid,
                    })}
                  />
                }
                iconPosition="right"
              />
            </div>
          </>
        )}
      </StyledContainer>
    </FormProvider>
  );
};

export default CreateVehicleWithChauffeurPage;
